import React, { useState, useRef, useCallback, useEffect } from 'react';
import {
  Box, Button, Typography, Card, CardContent,
  Stack, Chip, IconButton, Tooltip, CircularProgress,
  Alert, LinearProgress, Paper,
} from '@mui/material';
import MicIcon from '@mui/icons-material/Mic';
import StopIcon from '@mui/icons-material/Stop';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import { createMeeting, transcribeAudio } from '../api';

/* ── 3D Animated waveform ── */
const WaveformBars = ({ barCount = 36 }) => (
  <Box className="waveform" sx={{ my: 2.5 }}>
    {Array.from({ length: barCount }).map((_, i) => (
      <Box key={i} className="waveform-bar" sx={{ animationDelay: `${(i * 0.06).toFixed(2)}s` }} />
    ))}
  </Box>
);

const fmt = (s) =>
  `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;

export default function Recorder({ onMeetingReady, showSnackbar }) {
  useEffect(() => { document.title = 'Tanify — Voice Recorder'; }, []);

  const [isRecording, setIsRecording]       = useState(false);
  const [recordingTime, setRecordingTime]   = useState(0);
  const [recordedBlob, setRecordedBlob]     = useState(null);
  const [liveTranscript, setLiveTranscript] = useState('');
  const [title, setTitle]                   = useState('');
  const [status, setStatus]                 = useState('idle');
  const [error, setError]                   = useState('');
  const [uploadMode, setUploadMode]         = useState(false);
  const [isPlaying, setIsPlaying]           = useState(false);

  const mediaRecorderRef = useRef(null);
  const recognitionRef   = useRef(null);
  const chunksRef        = useRef([]);
  const timerRef         = useRef(null);
  const audioRef         = useRef(null);
  const fileInputRef     = useRef(null);
  const liveTextRef      = useRef('');
  const isRecordingRef   = useRef(false);
  const streamRef        = useRef(null);

  useEffect(() => { isRecordingRef.current = isRecording; }, [isRecording]);

  const startRecording = useCallback(async () => {
    setError(''); setLiveTranscript(''); liveTextRef.current = ''; setUploadMode(false);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          // noiseSuppression gates out soft/quiet speech, and the browser's own
          // AGC fights our gain chain — disable both so we can capture small voices
          // and control the level ourselves below.
          noiseSuppression: false,
          autoGainControl: false,
          sampleRate: 48000,
          channelCount: 1,
        },
      });
      streamRef.current = stream;
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const source = audioContext.createMediaStreamSource(stream);

      // ── Sensitivity chain: pre-gain → compressor → makeup gain ──
      // The compressor lifts quiet passages and reins in loud peaks so soft
      // voices are clearly captured without the loud parts clipping/distorting.
      const gainNode = audioContext.createGain();
      gainNode.gain.value = 2.2;                 // pre-amp

      const compressor = audioContext.createDynamicsCompressor();
      compressor.threshold.value = -50;          // start compressing early (quiet input)
      compressor.knee.value = 40;                // smooth onset
      compressor.ratio.value = 12;               // strong compression
      compressor.attack.value = 0.003;
      compressor.release.value = 0.25;

      const makeupGain = audioContext.createGain();
      makeupGain.gain.value = 3.0;               // bring the compressed signal back up

      const destination = audioContext.createMediaStreamDestination();
      source.connect(gainNode);
      gainNode.connect(compressor);
      compressor.connect(makeupGain);
      makeupGain.connect(destination);
      const boostedStream = destination.stream;
      const supportedMimeTypes = ['audio/webm;codecs=opus','audio/webm','audio/mp4','audio/aac','audio/ogg','audio/mpeg','audio/wav'];
      const mimeType = supportedMimeTypes.find(t => MediaRecorder.isTypeSupported(t)) || '';
      const recorder = new MediaRecorder(boostedStream, { mimeType: mimeType || undefined, audioBitsPerSecond: 256000 });
      mediaRecorderRef.current = recorder; chunksRef.current = [];
      recorder.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      recorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: mimeType || 'audio/webm' });
        setRecordedBlob(blob);
        if (audioRef.current) audioRef.current.src = URL.createObjectURL(blob);
        stream.getTracks().forEach(t => t.stop());
        boostedStream.getTracks().forEach(t => t.stop());
        audioContext.close();
        clearInterval(timerRef.current);
      };
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      if (SpeechRecognition) {
        const recognition = new SpeechRecognition();
        recognition.continuous = true; recognition.interimResults = true; recognition.lang = 'en-US'; recognition.maxAlternatives = 1;
        let finalizedText = '';
        recognition.onresult = (event) => {
          let interimText = '';
          for (let i = event.resultIndex; i < event.results.length; i++) {
            const transcript = event.results[i][0].transcript;
            if (event.results[i].isFinal) { finalizedText += transcript + ' '; } else { interimText = transcript; }
          }
          const combined = (finalizedText + interimText).trim();
          liveTextRef.current = combined; setLiveTranscript(combined);
        };
        recognition.onerror = (e) => { if (e.error === 'no-speech' || e.error === 'aborted') return; };
        recognition.onend = () => { if (isRecordingRef.current) { try { recognition.start(); } catch {} } };
        recognition.start(); recognitionRef.current = recognition;
      }
      recorder.start(250);
      setIsRecording(true); setStatus('recording'); setRecordingTime(0);
      timerRef.current = setInterval(() => setRecordingTime(t => t + 1), 1000);
    } catch (err) {
      setError('🎤 Microphone access denied. Please allow microphone permission and try again.');
    }
  }, []);

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') mediaRecorderRef.current.stop();
    if (recognitionRef.current) { try { recognitionRef.current.stop(); } catch {} recognitionRef.current = null; }
    if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop());
    clearInterval(timerRef.current);
    setIsRecording(false); setStatus('idle');
  }, []);

  const handleFileUpload = useCallback((e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setRecordedBlob(file);
    if (audioRef.current) audioRef.current.src = URL.createObjectURL(file);
    setTitle(file.name.replace(/\.[^/.]+$/, ''));
    setUploadMode(true); setStatus('idle'); setError(''); setLiveTranscript('');
  }, []);

  const handleSubmit = useCallback(async () => {
    if (!recordedBlob && !liveTranscript.trim()) { setError('Record audio or upload a file first.'); return; }
    setError(''); setStatus('processing');
    try {
      let finalText = '', finalSegments = [];
      if (liveTranscript.trim() && !uploadMode) {
        finalText = liveTranscript.trim();
        finalSegments = [{ speaker: 'Speaker 1', start: 0, end: recordingTime || 60, text: finalText }];
        showSnackbar('✅ Using live voice transcript…', 'info');
      } else if (recordedBlob) {
        showSnackbar('🎙️ Transcribing audio with AI…', 'info');
        try {
          const aiResult = await transcribeAudio(recordedBlob);
          finalText = aiResult.text || aiResult.full_text || '';
          if (!finalText.trim()) throw new Error('AI returned an empty transcript. Please try again with a clearer voice.');
          finalSegments = aiResult.segments || [];
        } catch (aiErr) {
          if (liveTranscript.trim()) {
            finalText = liveTranscript.trim();
            finalSegments = [{ speaker: 'Speaker 1', start: 0, end: recordingTime || 60, text: finalText }];
            showSnackbar('⚠️ AI unavailable — using live transcript instead.', 'warning');
          } else {
            throw new Error('Transcription failed: ' + aiErr.message);
          }
        }
      }
      if (!finalText) throw new Error('No speech detected. Please try again and speak clearly.');
      showSnackbar('💾 Saving meeting to database…', 'info');
      const meeting = await createMeeting({
        title: title.trim() || `Meeting ${new Date().toLocaleDateString()} ${new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`,
        transcript: finalSegments, full_text: finalText, language: 'auto',
      });
      setStatus('done');
      showSnackbar('🎉 Transcription complete!', 'success');
      setTimeout(() => onMeetingReady(meeting), 600);
    } catch (err) {
      setStatus('error'); setError(err.message || 'Failed to process recording.');
    }
  }, [recordedBlob, liveTranscript, uploadMode, title, recordingTime, showSnackbar, onMeetingReady]);

  const reset = useCallback(() => {
    if (isRecording) stopRecording();
    setRecordedBlob(null); setStatus('idle'); setError(''); setLiveTranscript('');
    liveTextRef.current = ''; setTitle(''); setRecordingTime(0); setUploadMode(false);
    if (audioRef.current) audioRef.current.src = '';
  }, [isRecording, stopRecording]);

  const hasContent = recordedBlob || liveTranscript.trim();
  const isProcessing = status === 'processing';

  return (
    <Box sx={{ maxWidth: 820, mx: 'auto' }}>
      <Card className="glass-card" sx={{ mb: 3, overflow: 'visible' }}>
        <CardContent sx={{ p: { xs: 3, md: 5 }, textAlign: 'center' }}>

          {/* ══ IDLE: Two 3D tilt cards ══ */}
          {!isRecording && !hasContent && (
            <Box className="fade-in-up">
              <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#374151', letterSpacing: 1.5, textTransform: 'uppercase', mb: 3, fontFamily: '"JetBrains Mono", monospace' }}>
                Choose input method
              </Typography>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} justifyContent="center" alignItems="stretch">

                <Paper elevation={0} className="tilt-card" onClick={startRecording} sx={{
                  flex: 1, p: 4, borderRadius: '20px', cursor: 'pointer',
                  background: 'rgba(124,58,237,0.04)', border: '1px solid rgba(124,58,237,0.15)',
                  position: 'relative', overflow: 'hidden',
                  '&:hover': { background: 'rgba(124,58,237,0.09)', borderColor: 'rgba(124,58,237,0.4)', boxShadow: '0 0 40px rgba(124,58,237,0.18)' },
                  '&::before': { content: '""', position: 'absolute', top: 0, left: 0, width: '60%', height: '55%', background: 'radial-gradient(ellipse at 0% 0%, rgba(124,58,237,0.1), transparent 70%)', pointerEvents: 'none' },
                }}>
                  <Box sx={{ width: 72, height: 72, borderRadius: '50%', background: 'rgba(124,58,237,0.12)', border: '1px solid rgba(124,58,237,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 2.5, boxShadow: '0 0 24px rgba(124,58,237,0.18)' }}>
                    <MicIcon sx={{ fontSize: 32, color: '#8B5CF6' }} />
                  </Box>
                  <Typography variant="h6" fontWeight={800} mb={1} sx={{ fontFamily: '"Space Grotesk", sans-serif' }}>Live Recording</Typography>
                  <Typography variant="body2" sx={{ color: '#64748B', mb: 2.5, lineHeight: 1.65 }}>Record your meeting audio in real-time with live speech-to-text preview.</Typography>
                  <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.8, px: 2, py: 0.8, borderRadius: '10px', background: 'rgba(124,58,237,0.12)', border: '1px solid rgba(124,58,237,0.2)' }}>
                    <Box sx={{ width: 6, height: 6, borderRadius: '50%', background: '#8B5CF6', animation: 'pulseDot 1.5s ease-in-out infinite' }} />
                    <Typography variant="caption" sx={{ color: '#A78BFA', fontWeight: 700, fontSize: 12 }}>Click to start</Typography>
                  </Box>
                </Paper>

                <Paper elevation={0} className="tilt-card" onClick={() => fileInputRef.current?.click()} sx={{
                  flex: 1, p: 4, borderRadius: '20px', cursor: 'pointer',
                  background: 'rgba(6,182,212,0.04)', border: '1px solid rgba(6,182,212,0.15)',
                  position: 'relative', overflow: 'hidden',
                  '&:hover': { background: 'rgba(6,182,212,0.09)', borderColor: 'rgba(6,182,212,0.4)', boxShadow: '0 0 40px rgba(6,182,212,0.18)' },
                  '&::before': { content: '""', position: 'absolute', top: 0, left: 0, width: '60%', height: '55%', background: 'radial-gradient(ellipse at 0% 0%, rgba(6,182,212,0.1), transparent 70%)', pointerEvents: 'none' },
                }}>
                  <Box sx={{ width: 72, height: 72, borderRadius: '50%', background: 'rgba(6,182,212,0.12)', border: '1px solid rgba(6,182,212,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 2.5, boxShadow: '0 0 24px rgba(6,182,212,0.18)' }}>
                    <CloudUploadIcon sx={{ fontSize: 32, color: '#06B6D4' }} />
                  </Box>
                  <Typography variant="h6" fontWeight={800} mb={1} sx={{ fontFamily: '"Space Grotesk", sans-serif' }}>Upload Audio</Typography>
                  <Typography variant="body2" sx={{ color: '#64748B', mb: 2.5, lineHeight: 1.65 }}>MP3, M4A, WAV, MP4, MOV, FLAC — drop or browse any format.</Typography>
                  <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.8, px: 2, py: 0.8, borderRadius: '10px', background: 'rgba(6,182,212,0.12)', border: '1px solid rgba(6,182,212,0.2)' }}>
                    <Typography variant="caption" sx={{ color: '#22D3EE', fontWeight: 700, fontSize: 12 }}>↑ Click to browse</Typography>
                  </Box>
                </Paper>

              </Stack>
              <input type="file" accept="audio/*,video/*,.m4a,.mp3,.wav,.mp4,.mov,.ogg,.flac" style={{ display: 'none' }} ref={fileInputRef} onChange={handleFileUpload} />
            </Box>
          )}

          {/* ══ RECORDING STATE ══ */}
          {isRecording && (
            <Box className="fade-in-up">
              <Stack direction="row" spacing={1} justifyContent="center" alignItems="center" mb={0.5}>
                <FiberManualRecordIcon sx={{ color: '#EF4444', fontSize: 11, animation: 'pulseDot 1s ease-in-out infinite' }} />
                <Typography sx={{ fontSize: 11, fontWeight: 900, color: '#EF4444', fontFamily: '"JetBrains Mono", monospace', letterSpacing: 2 }}>REC</Typography>
              </Stack>
              <Typography sx={{ fontSize: '3.5rem', fontWeight: 800, fontFamily: '"JetBrains Mono", monospace', color: '#EF4444', lineHeight: 1.1, mb: 0.5, textShadow: '0 0 30px rgba(239,68,68,0.4)' }}>
                {fmt(recordingTime)}
              </Typography>
              <Typography variant="caption" sx={{ color: '#475569', mb: 1.5, display: 'block' }}>Recording in progress…</Typography>
              <WaveformBars barCount={36} />
              {liveTranscript && (
                <Paper elevation={0} sx={{ p: 2.5, mt: 2, mb: 2, borderRadius: '16px', background: 'rgba(124,58,237,0.07)', border: '1px solid rgba(124,58,237,0.18)', borderLeft: '3px solid #7C3AED', maxHeight: 130, overflow: 'auto', textAlign: 'left' }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: '#A78BFA', display: 'block', mb: 0.5, fontSize: 10, letterSpacing: 0.5, textTransform: 'uppercase', fontFamily: '"JetBrains Mono", monospace' }}>Live Transcript</Typography>
                  <Typography variant="body2" sx={{ color: '#E2E8F0', lineHeight: 1.8 }}>{liveTranscript}</Typography>
                </Paper>
              )}
              {!liveTranscript && recordingTime > 2 && (
                <Typography variant="caption" sx={{ color: '#475569', display: 'block', mb: 2 }}>Speak now — your voice is being captured…</Typography>
              )}
              <Box sx={{ mt: 3 }}>
                <Box className="record-pulse" sx={{ display: 'inline-block', borderRadius: '50%' }}>
                  <IconButton onClick={stopRecording} id="btn-stop-recording" sx={{ width: 84, height: 84, background: 'linear-gradient(135deg, #EF4444, #DC2626)', boxShadow: '0 0 32px rgba(239,68,68,0.45)', '&:hover': { background: '#B91C1C' }, transition: 'all 0.2s' }}>
                    <StopIcon sx={{ fontSize: 38, color: '#fff' }} />
                  </IconButton>
                </Box>
                <Typography variant="caption" sx={{ color: '#475569', display: 'block', mt: 1.5 }}>Click to stop recording</Typography>
              </Box>
            </Box>
          )}

          {/* ══ REVIEW STATE ══ */}
          {!isRecording && hasContent && status !== 'done' && (
            <Box className="fade-in-up">
              <Box sx={{ width: 68, height: 68, borderRadius: '50%', mx: 'auto', mb: 2.5, background: 'rgba(16,185,129,0.1)', border: '1px solid rgba(16,185,129,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 0 24px rgba(16,185,129,0.15)' }}>
                <CheckCircleIcon sx={{ fontSize: 38, color: '#10B981' }} />
              </Box>
              <Typography variant="h6" fontWeight={700} mb={0.5} sx={{ fontFamily: '"Space Grotesk", sans-serif' }}>
                {uploadMode ? '📁 Audio File Ready' : '🎙️ Recording Complete'}
              </Typography>
              {recordingTime > 0 && (
                <Chip label={`Duration: ${fmt(recordingTime)}`} size="small" sx={{ background: 'rgba(16,185,129,0.12)', color: '#10B981', fontWeight: 700, mb: 2.5, fontFamily: '"JetBrains Mono", monospace' }} />
              )}
              {recordedBlob && (
                <Box sx={{ width: '100%', maxWidth: 480, mx: 'auto', mb: 3 }}>
                  {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
                  <audio ref={audioRef} autoPlay={false} style={{ display: 'none' }} onEnded={() => setIsPlaying(false)} onPause={() => setIsPlaying(false)} onPlay={() => setIsPlaying(true)} />
                  <Box sx={{ p: 2.5, borderRadius: '16px', background: 'rgba(12,12,24,0.8)', border: '1px solid rgba(139,92,246,0.2)', display: 'flex', alignItems: 'center', gap: 2 }}>
                    <IconButton onClick={() => { if (audioRef.current?.paused) { audioRef.current.play(); setIsPlaying(true); } else { audioRef.current?.pause(); setIsPlaying(false); } }} sx={{ width: 48, height: 48, flexShrink: 0, background: 'linear-gradient(135deg, #7C3AED, #06B6D4)', '&:hover': { background: 'linear-gradient(135deg, #8B5CF6, #22D3EE)' } }}>
                      {isPlaying ? <PauseIcon sx={{ fontSize: 26, color: '#fff' }} /> : <PlayArrowIcon sx={{ fontSize: 26, color: '#fff' }} />}
                    </IconButton>
                    <Box sx={{ flex: 1, textAlign: 'left' }}>
                      <Typography variant="body2" fontWeight={700} sx={{ color: '#E2E8F0' }}>{uploadMode ? 'Uploaded Audio' : 'Your Recording'}</Typography>
                      <Typography variant="caption" sx={{ color: '#475569', fontFamily: '"JetBrains Mono", monospace' }}>{isPlaying ? '▶ Playing…' : 'Click ▶ to preview'}</Typography>
                    </Box>
                  </Box>
                </Box>
              )}
              {liveTranscript && (
                <Paper elevation={0} sx={{ p: 2.5, mb: 3, borderRadius: '16px', background: 'rgba(124,58,237,0.06)', border: '1px solid rgba(124,58,237,0.12)', borderLeft: '3px solid #7C3AED', maxHeight: 170, overflow: 'auto', textAlign: 'left', maxWidth: 600, mx: 'auto' }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: '#A78BFA', display: 'block', mb: 1, fontSize: 10, textTransform: 'uppercase', fontFamily: '"JetBrains Mono", monospace' }}>Captured Transcript</Typography>
                  <Typography variant="body2" sx={{ color: '#E2E8F0', lineHeight: 1.8 }}>{liveTranscript}</Typography>
                </Paper>
              )}
              <Box sx={{ maxWidth: 500, mx: 'auto', mb: 3 }}>
                <Typography variant="caption" sx={{ color: '#374151', fontWeight: 700, mb: 1, display: 'block', textAlign: 'left', fontSize: 10, textTransform: 'uppercase', fontFamily: '"JetBrains Mono", monospace', letterSpacing: 0.5 }}>Meeting Title</Typography>
                <input className="neo-input" placeholder="Enter meeting title…" value={title} onChange={(e) => setTitle(e.target.value)} aria-label="Meeting title" />
              </Box>
              {error && <Alert severity="error" sx={{ mb: 2, maxWidth: 500, mx: 'auto', textAlign: 'left' }}>{error}</Alert>}
              {isProcessing && (
                <Box sx={{ mb: 3, maxWidth: 500, mx: 'auto' }}>
                  <Alert severity="info" sx={{ mb: 1.5 }}>⏳ Processing your recording — please wait…</Alert>
                  <LinearProgress />
                </Box>
              )}
              <Stack direction="row" spacing={2} justifyContent="center">
                <Button variant="contained" size="large" onClick={handleSubmit} disabled={isProcessing}
                  startIcon={isProcessing ? <CircularProgress size={18} color="inherit" /> : <CloudUploadIcon />}
                  id="btn-submit-recording"
                  sx={{ py: 1.5, px: 5, borderRadius: '14px', fontSize: 15, fontWeight: 700, background: 'linear-gradient(135deg, #7C3AED, #06B6D4)', boxShadow: '0 4px 24px rgba(124,58,237,0.35)', '&:hover': { background: 'linear-gradient(135deg, #8B5CF6, #22D3EE)', boxShadow: '0 6px 32px rgba(124,58,237,0.5)' } }}>
                  {isProcessing ? 'Processing…' : 'Submit & Transcribe'}
                </Button>
                <Tooltip title="Start over">
                  <IconButton onClick={reset} disabled={isProcessing} sx={{ border: '1px solid rgba(139,92,246,0.2)', borderRadius: '12px', px: 2, '&:hover': { borderColor: '#EF4444', color: '#EF4444', background: 'rgba(239,68,68,0.06)' } }}>
                    <DeleteIcon />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Box>
          )}

          {/* ══ DONE STATE ══ */}
          {status === 'done' && (
            <Box className="fade-in-up" sx={{ py: 4 }}>
              <Box sx={{ width: 76, height: 76, borderRadius: '50%', mx: 'auto', mb: 2.5, background: 'rgba(16,185,129,0.12)', border: '1px solid rgba(16,185,129,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 0 30px rgba(16,185,129,0.2)' }}>
                <CheckCircleIcon sx={{ fontSize: 44, color: '#10B981' }} />
              </Box>
              <Typography variant="h6" fontWeight={700} sx={{ color: '#10B981', mb: 1, fontFamily: '"Space Grotesk", sans-serif' }}>Transcription Complete!</Typography>
              <Typography variant="body2" sx={{ color: '#475569', mb: 3 }}>Switching to your transcript…</Typography>
              <Button variant="outlined" onClick={reset} sx={{ borderRadius: '12px', px: 4 }}>Record Another</Button>
            </Box>
          )}

        </CardContent>
      </Card>

      {error && !hasContent && <Alert severity="error" sx={{ borderRadius: '14px', mb: 2 }}>{error}</Alert>}

      <Box sx={{ mt: 3, pb: 2, textAlign: 'center' }}>
        <Typography variant="caption" sx={{ color: '#1E293B', fontSize: 10, fontFamily: '"JetBrains Mono", monospace' }}>
          TANIFY v1.1.9 · VOICE RECORDING + LIVE TRANSCRIPTION
        </Typography>
      </Box>
    </Box>
  );
}
